import {isNullOrUndefined} from 'util';
import {Component, ElementRef, EventEmitter, forwardRef, Input, ViewChild} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {DaterangePickerComponent, DaterangepickerConfig} from 'ng2-daterangepicker';
import {DATE_FORMAT} from './date-range-picker.module';
import * as moment from 'moment';
import {TranslateManager} from '../../../../../common/translate';

export const DATE_RANGE_PICKER_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DateRangePickerComponent),
    multi: true
};

@Component({
    selector: 'tfl-daterangepicker',
    providers: [DATE_RANGE_PICKER_CONTROL_VALUE_ACCESSOR],
    template: `
        <div [id]="id"
             class="input-group period-block"
             daterangepicker
             (selected)="onDateRangeSelect($event)"
             (cancelDaterangepicker)="onDateRangeCancel()">

            <input #dateInput
                   [name]="name"
                   class="form-control"
                   [value]="getViewValue()"
                   (keyup)="checkValue()"
                   (keyup.enter)="applyTypedValue()"
                   (blur)="applyTypedValue()"
                   placeholder="{{ 'TRANSFERS.HISTORY.FILTER.PLACEHOLDER.PERIOD' | translate }}">

            <span class="input-group-btn">
                <a type="button" class="btn btn-default calendar-button">
                    <i class="fa fa-calendar"></i>
                </a>
            </span>
        </div>
    `
})
export class DateRangePickerComponent implements ControlValueAccessor {

    @Input('id') id: string;
    @Input('name') name: string;
    @Input('beginPropName') beginPropName: string = 'beginDate';
    @Input('endPropName') endPropName: string = 'endDate';

    constructor(daterangepickerOptions: DaterangepickerConfig, translations: TranslateManager) {
        // Reset global config on every instantiation so that limits set by other
        // pages (e.g. dateLimit: { months: 12 } from Directors or Provider Reports)
        // do not carry over here.
        daterangepickerOptions.settings = {
            opens: 'down',
            drops: 'down',
            showDropdowns: true,
            dateLimit: false,
            minYear: 2000,
            maxYear: new Date().getFullYear(),
            locale: {
                format: DATE_FORMAT,
                firstDay: 1,
                cancelLabel: translations.getValue('COMMON.CLEAR')
            },
            alwaysShowCalendars: false
        };
    }

    @ViewChild(DaterangePickerComponent) picker: DaterangePickerComponent;

    @ViewChild('dateInput') dateInput: ElementRef;

    value: {} = {};

    inputValue: string;

    onChange: EventEmitter<any> = new EventEmitter<any>();
    onTouchedCallback: EventEmitter<any> = new EventEmitter<any>();

    writeValue(obj: any): void {
        if (obj
            && obj.hasOwnProperty(this.beginPropName)
            && obj.hasOwnProperty(this.endPropName)) {
            this.value = {
                [this.beginPropName]: obj[this.beginPropName],
                [this.endPropName]: obj[this.endPropName]
            };

            if (!!this.picker && !!this.picker.datePicker) {
                this.picker.datePicker.setStartDate(obj[this.beginPropName]);
                this.picker.datePicker.setEndDate(obj[this.endPropName]);
            }
        } else {
            this.value = undefined;

            if (!!this.picker && !!this.picker.datePicker) {
                this.picker.datePicker.setStartDate(undefined);
                this.picker.datePicker.setEndDate(undefined);
            }
        }
    }

    registerOnChange(fn: any): void {
        this.onChange.subscribe(fn);
    }

    registerOnTouched(fn: any): void {
        this.onTouchedCallback.subscribe(fn);
    }

    isEmpty(): boolean {
        return this.value === undefined;
    }

    getViewValue(): string {
        if (this.value) {
            return `${this.value[this.beginPropName]} - ${this.value[this.endPropName]}`;
        } else {
            return '';
        }
    }

    onDateRangeSelect(value: any) {
        /* Save and emit new value */
        this.onChange.emit(this.value = {
            [this.beginPropName]: isNullOrUndefined(value.start) ? undefined : value.start.format(DATE_FORMAT),
            [this.endPropName]: isNullOrUndefined(value.end) ? undefined : value.end.format(DATE_FORMAT)
        });
    }

    onDateRangeCancel() {
        /* Save and emit empty value */
        this.onChange.emit(this.value = undefined);
    }

    /**
     * A period typed by hand ("dd.MM.yyyy - dd.MM.yyyy") is applied on Enter or when leaving the field, as if it had
     * been picked in the calendar. Anything else that is not empty goes back to the current value.
     */
    applyTypedValue() {
        const typed = (this.dateInput.nativeElement.value || '').trim();
        if (typed === '' || typed === this.getViewValue()) {
            return;
        }
        const parts = typed.split(/\s*-\s*/);
        const start = parts.length === 2 ? moment(parts[0], DATE_FORMAT, true) : null;
        const end = parts.length === 2 ? moment(parts[1], DATE_FORMAT, true) : null;
        if (!start || !end || !start.isValid() || !end.isValid() || end.isBefore(start)) {
            this.dateInput.nativeElement.value = this.getViewValue();
            this.inputValue = this.dateInput.nativeElement.value;
            return;
        }
        if (!!this.picker && !!this.picker.datePicker) {
            this.picker.datePicker.setStartDate(start);
            this.picker.datePicker.setEndDate(end);
        }
        this.onDateRangeSelect({start: start, end: end});
        this.inputValue = this.getViewValue();
    }

    checkValue() {
        if (this.inputValue !== this.dateInput.nativeElement.value) {
            this.inputValue = this.dateInput.nativeElement.value;

            if (this.inputValue === '') {
                this.onDateRangeCancel();
            }
        }
    }
}


// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/angular-router-loader/src!./src/dashboard/app/shared/input/daterangepicker/date-range-picker.component.ts